import { Box, VStack, HStack, Textarea, Button } from "@chakra-ui/react";
import useTicketComments from "hooks/useTicketComments";
import { SupportTicket } from "models/support";
import { FC, useState } from "react";
import { ExternalLinkIcon } from "@chakra-ui/icons";

import TicketComment from "./TicketComment";
import useMarkTicketAsRead from "hooks/useMarkTicketAsRead";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons";
import useZendeskSSO from "hooks/useZendeskSSO";
import { ZENDESK_BASE_URL } from "util/config";

const TicketComments: FC<{ ticket: SupportTicket; unread: boolean }> = ({
    ticket,
    unread,
}) => {
    const { data: comments, isError, isLoading } = useTicketComments(ticket);
    const { markAsRead } = useMarkTicketAsRead();
    const [body, setBody] = useState<string>();
    const query = useQueryClient();

    if (!isLoading && !isError && unread) {
        markAsRead(ticket.id);
    }

    const update = useAuthenticatedRequestCreator<void, any>((data) => ({
        method: "post",
        url: `/support/tickets/${ticket.id}/comments`,
        data: { body },
    }));

    const { mutateAsync: submitComment, isLoading: isSubmitCommentLoading } =
        useMutation(update, {
            onSuccess: () => {
                query.invalidateQueries(["comments", ticket.id]);
            },
        });

    const makeZendeskLink = useZendeskSSO();

    const openZendeskComment = async () => {
        const commentUrl = `${ZENDESK_BASE_URL}/hc/en-us/requests/${ticket.id}`;
        const zendeskLink = await makeZendeskLink(commentUrl);
        window.open(zendeskLink, "_blank");
    };

    return isError ? (
        <Box fontSize={24} color="error">
            Failed to load comments
        </Box>
    ) : (
        <VStack
            mx={4}
            spacing={0}
            w="calc(100% - 3rem)"
            h="100%"
            minH="10rem"
            align="start"
            marginLeft="3rem"
            marginRight={2}
            bgColor="rgb(34, 39, 44)"
        >
            {comments?.map((comment, i) => (
                <TicketComment
                    key={comment.id}
                    comment={comment}
                    isTop={i === 0}
                />
            ))}
            <VStack
                padding={4}
                width="100%"
                borderTop="1px solid gray"
                spacing={2}
                alignItems="end"
            >
                <Textarea
                    color="white"
                    disabled={ticket.isSolved}
                    placeholder={
                        ticket.isSolved
                            ? "This issue is closed. Please open a new support request"
                            : "Send a new message"
                    }
                    noOfLines={ticket.isSolved ? 1 : 2}
                    value={body}
                    onChange={(e) => {
                        setBody(e.target.value);
                    }}
                />
                {!ticket.isSolved && (
                    <HStack>
                        <Button onClick={openZendeskComment}>
                            <FontAwesomeIcon icon={faPaperclip} />
                            <Box marginX={2}>Add attachments</Box>
                            <ExternalLinkIcon />
                        </Button>
                        <Button
                            bgColor="var(--dcs-blue)"
                            onClick={submitComment}
                            disabled={isSubmitCommentLoading}
                        >
                            Send
                        </Button>
                    </HStack>
                )}
            </VStack>
        </VStack>
    );
};

export default TicketComments;
